import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ItemService, ItemPagedResult } from '../../../services/item.service';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { StockService } from '../../../services/stock.service';
import { WarehousesService } from '../../../services/warehouses.service';
import { Item } from '../../../models/Item';
import { Stock } from '../../../models/Stock';
import { Warehouse } from '../../../models/Warehouse';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';

@Component({
  selector: 'app-items',
  templateUrl: './items.component.html',
  styleUrls: ['./items.component.css']
})
export class ItemsComponent implements OnInit {
  // Configuração da tabela
  tableTitle = 'Gestão de Itens';
  showSearch = true;
  searchPlaceholder = 'Buscar itens...';
  clickable = true;

  // Colunas da tabela
  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Estoque', label: 'stockName' },
    { titulo: 'Almoxarifado', label: 'warehouseName' },
    { titulo: 'Quantidade', label: 'quantity' },
    { titulo: 'Custo Unitário', label: 'unitCost', format: 'currency' },
    { titulo: 'Preço de Custo', label: 'costPrice', format: 'currency' },
    { titulo: 'Preço de Venda', label: 'salePrice', format: 'currency' },
    { titulo: 'Moeda', label: 'currencyCode' },
    { titulo: 'Requer Aprovação', label: 'requiresApproval', format: 'boolean' },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Condição', label: 'condition' }
  ];

  // Ações da tabela
  actions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Transferir',
      icon: 'fas fa-right-left',
      action: 'transfer',
      class: 'btn-toggle'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  // Dados
  items: Item[] = [];
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  selectedItem: Item | null = null;
  loading = false;
  error: string | null = null;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  selectedStockId: number | string | null = '';
  selectedWarehouseId: number | string | null = '';
  selectedStatus: string | null = '';
  searchTerm = '';

  // Opções para filtros
  statusOptions: Status[] = [];

  constructor(
    private router: Router,
    private itemService: ItemService,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadStocks();
    this.loadWarehouses();
    this.loadStatuses();
    this.loadItems();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Item').subscribe({
      next: (statuses) => {
        if (statuses && statuses.length > 0) {
          this.statusOptions = statuses;
        } else {
          this.statusOptions = this.getDefaultStatusOptions();
        }
      },
      error: (error) => {
        console.error('Error loading statuses:', error);
        this.statusOptions = this.getDefaultStatusOptions();
      }
    });
  }

  private getDefaultStatusOptions(): Status[] {
    return ['Disponível', 'Em Uso', 'Danificado', 'Vencido', 'Em Manutenção', 'Em Trânsito']
      .map((name, i) => ({ id: i + 1, name, description: '', entityType: 'Item', isActive: true } as Status));
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
        this.stocks = [];
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar almoxarifados:', error);
        this.warehouses = [];
      }
    });
  }

  loadItems(): void {
    this.loading = true;
    this.error = null;

    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };

    this.itemService.getAllItems(options).subscribe({
      next: (result: ItemPagedResult) => {
        this.items = this.sortItemsBySearchRelevance(result.items || [], this.searchTerm);
        this.totalItems = Number(result.totalItems || 0);
        this.totalPages = Number(result.totalPages || 0);
        this.currentPage = Number(result.page || 1);
        this.pageSize = Number(result.pageSize || this.pageSize);
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar itens:', error);
        this.error = `Erro ao carregar itens: ${error.message || 'Erro desconhecido'}`;
        this.loading = false;
        this.items = [];
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilters(true);
  }

  onStockFilterChange(stockId: number | string | null): void {
    // Manter string vazia para "Todos os estoques"
    this.selectedStockId = stockId;
    this.applyFilters(true);
  }

  onWarehouseFilterChange(warehouseId: number | string | null): void {
    // Manter string vazia para "Todos os almoxarifados"
    this.selectedWarehouseId = warehouseId;
    this.applyFilters(true);
  }

  onStatusFilterChange(status: string | null): void {
    // Manter string vazia para "Todos os status"
    this.selectedStatus = status;
    this.applyFilters(true);
  }

  applyFilters(resetToFirstPage: boolean = true): void {
    this.loading = true;
    this.error = null;
    if (resetToFirstPage) this.currentPage = 1;

    const hasStock     = !!this.selectedStockId && this.selectedStockId !== '' && Number(this.selectedStockId) > 0;
    const hasWarehouse = !!this.selectedWarehouseId && this.selectedWarehouseId !== '' && Number(this.selectedWarehouseId) > 0;
    const hasStatus    = !!this.selectedStatus && this.selectedStatus !== '' && !!this.selectedStatus.trim();
    const hasSearch    = !!this.searchTerm && !!this.searchTerm.trim();
    const activeCount  = [hasStock, hasWarehouse, hasStatus, hasSearch].filter(Boolean).length;

    // Status has no combined API endpoint; 2+ active filters cause pagination mismatch — load all and paginate client-side
    const needsClientSide = hasStatus || activeCount > 1;
    const options: PaginationOptions = needsClientSide
      ? { page: 1, pageSize: 1000 }
      : { page: this.currentPage, pageSize: this.pageSize };

    let observable: Observable<ItemPagedResult>;
    if (hasStock) {
      observable = this.itemService.getItemsByStock(Number(this.selectedStockId), options);
    } else if (hasWarehouse) {
      observable = this.itemService.getItemsByWarehouse(Number(this.selectedWarehouseId), options);
    } else if (hasSearch) {
      observable = this.itemService.getAllItemsWithNameFilter(this.searchTerm, options);
    } else {
      observable = this.itemService.getAllItems(options);
    }

    observable.subscribe({
      next: (result: ItemPagedResult) => {
        let filtered = result.items || [];
        if (hasStatus) {
          filtered = filtered.filter(item => (item.status || '').toLowerCase().trim() === (this.selectedStatus || '').toLowerCase().trim());
        }
        if (hasStock && hasWarehouse) {
          filtered = filtered.filter(item => item.warehouseId === Number(this.selectedWarehouseId));
        }
        this.items = this.sortItemsBySearchRelevance(filtered, this.searchTerm);
        if (needsClientSide) {
          this.totalItems  = 0; // app-table uses internal pagination over full array
          this.hasNextPage = false;
          this.hasPreviousPage = false;
        } else {
          this.totalItems    = Number(result.totalItems || 0);
          this.totalPages    = Number(result.totalPages || 0);
          this.currentPage   = Number(result.page || 1);
          this.pageSize      = Number(result.pageSize || this.pageSize);
          this.hasNextPage   = this.currentPage < this.totalPages;
          this.hasPreviousPage = this.currentPage > 1;
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao aplicar filtro:', error);
        this.error = `Erro ao carregar itens: ${error.message || 'Erro desconhecido'}`;
        this.loading = false;
        this.items = [];
      }
    });
  }

  clearFilters(): void {
    this.selectedStockId = '';
    this.selectedWarehouseId = '';
    this.selectedStatus = '';
    this.searchTerm = '';
    this.loadItems();
  }

  // Método para tentar recarregar dados em caso de erro
  retryLoad(): void {
    this.loadItems();
  }

  onActionClick(actionData: {action: string, item: any}): void {
    const { action, item } = actionData;
    
    switch (action) {
      case 'view':
        this.viewItem(item);
        break;
      case 'edit':
        this.editItem(item);
        break;
      case 'transfer':
        this.transferItem(item);
        break;
      case 'delete':
        this.deleteItem(item);
        break;
      default:
    }
  }

  viewItem(item: Item): void {
    this.router.navigate(['/stock/items', item.id, 'view']);
  }

  editItem(item: Item): void {
    this.router.navigate(['/stock/items', item.id, 'edit']);
  }

  transferItem(item: Item): void {
    this.router.navigate(['/stock/items', item.id, 'view'], { 
      queryParams: { action: 'transfer' } 
    });
  }


  deleteItem(item: Item): void {
    if (confirm(`Tem certeza que deseja excluir o item "${item.name}"?`)) {
      this.itemService.deleteItem(item.id).subscribe({
        next: () => {
          this.loadItems();
          // TODO: Mostrar mensagem de sucesso
        },
        error: (error) => {
          console.error('Erro ao excluir item:', error);
          // TODO: Mostrar mensagem de erro
        }
      });
    }
  }

  newItem(): void { 
    this.router.navigate(['/stock/items/new']); 
  }

  getStockName(stockId: number): string {
    const stock = this.stocks.find(s => s.id === stockId);
    return stock ? stock.name : 'N/A';
  }

  getWarehouseName(warehouseId?: number): string {
    if (!warehouseId) return 'N/A';
    const warehouse = this.warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'N/A';
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'disponível':
        return 'status-available';
      case 'em uso':
        return 'status-in-use';
      case 'danificado':
        return 'status-damaged';
      case 'vencido':
        return 'status-expired';
      case 'em manutenção':
        return 'status-maintenance';
      case 'em trânsito':
        return 'status-in-transit';
      default:
        return 'status-default';
    }
  }

  getConditionClass(condition?: string): string {
    if (!condition) return 'condition-default';
    
    switch (condition.toLowerCase()) {
      case 'novo':
        return 'condition-new';
      case 'usado':
        return 'condition-used';
      case 'danificado':
        return 'condition-damaged';
      case 'reparado':
        return 'condition-repaired';
      default:
        return 'condition-default';
    }
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.items = [...this.items].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFilters(false);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.applyFilters(false);
  }

  /**
   * Mantém no topo o item com melhor aderência ao texto pesquisado:
   * 1) nome exato, 2) começa com, 3) contém.
   */
  private sortItemsBySearchRelevance(items: Item[], term: string): Item[] {
    const normalizedTerm = (term || '').trim().toLowerCase();
    if (!normalizedTerm) {
      return items;
    }

    const score = (item: Item): number => {
      const name = String(item.name || '').trim().toLowerCase();
      if (!name) return 99;
      if (name === normalizedTerm) return 0;
      if (name.startsWith(normalizedTerm)) return 1;
      if (name.includes(normalizedTerm)) return 2;
      return 3;
    };

    return [...items].sort((a, b) => {
      const diff = score(a) - score(b);
      if (diff !== 0) return diff;
      return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
    });
  }
}
